// packages/shared/api-client/baseApi.js
// Base RTK Query API configuration

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";
import { getApiUrl } from "./config";

// Create a mutex to prevent multiple simultaneous refresh attempts
const mutex = new Mutex();

/**
 * Base query configuration
 */
const baseQuery = fetchBaseQuery({
  baseUrl: getApiUrl(),
  prepareHeaders: (headers, { getState }) => {
    const token = getState()?.auth?.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const delegationTenantId = getState()?.auth?.activeDelegationTenantId;
    if (delegationTenantId) {
      headers.set("X-Delegation-Target", delegationTenantId);
    }

    return headers;
  },
});

/**
 * Base query wrapper with automatic token refresh
 * Handles 401 errors by attempting to refresh the access token
 */
const baseQueryWithReauth = async (args, api, extraOptions) => {
  // Wait for any in-progress refresh to complete
  await mutex.waitForUnlock();

  // Try the initial request
  let result = await baseQuery(args, api, extraOptions);

  // If the tenant has been deactivated, force logout immediately
  if (
    result.error &&
    result.error.status === 403 &&
    result.error.data?.tenantDeactivated
  ) {
    console.log("[BaseAPI] Tenant deactivated, logging out");
    api.dispatch({ type: "auth/logout" });
    return result;
  }

  // If we get a 401 error, attempt to refresh the token
  if (result.error && result.error.status === 401) {
    console.log("[BaseAPI] 401 error detected, attempting token refresh");

    // Check if another request is already refreshing the token
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const state = api.getState();
        const refreshToken = state?.auth?.refreshToken;

        if (!refreshToken) {
          // No refresh token available - logout
          console.log("[BaseAPI] No refresh token available, logging out");
          api.dispatch({ type: "auth/logout" });
          return result;
        }

        console.log("[BaseAPI] Attempting to refresh token...");

        // Attempt to refresh the token
        const refreshResult = await baseQuery(
          {
            url: "/api/auth/refresh",
            method: "POST",
            body: { refreshToken },
          },
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          console.log("[BaseAPI] Token refresh successful");

          // Extract tokens from the nested response structure
          // Backend returns: { status, message, data: { user, tokens } }
          const { data } = refreshResult.data;

          if (!data || !data.tokens) {
            console.error(
              "[BaseAPI] Invalid refresh response structure:",
              refreshResult.data,
            );
            console.log(
              "[BaseAPI] Logging out due to invalid refresh response",
            );
            api.dispatch({ type: "auth/logout" });
            return result;
          }

          const { tokens } = data;
          const { accessToken, refreshToken: newRefreshToken } = tokens;

          if (!accessToken || !newRefreshToken) {
            console.error("[BaseAPI] Missing tokens in refresh response");
            console.log("[BaseAPI] Logging out due to missing tokens");
            api.dispatch({ type: "auth/logout" });
            return result;
          }

          console.log(
            "[BaseAPI] Updating tokens and user (with fresh permissions) in Redux",
          );
          // Update tokens in Redux state
          api.dispatch({
            type: "auth/updateTokens",
            payload: { accessToken, refreshToken: newRefreshToken },
          });
          if (data.user) {
            api.dispatch({ type: "auth/updateUser", payload: data.user });
          }

          console.log("[BaseAPI] Retrying original request with new token");
          // Retry the original request with the new token
          result = await baseQuery(args, api, extraOptions);

          if (result.error) {
            console.error(
              "[BaseAPI] Retry after refresh still failed:",
              result.error,
            );
          } else {
            console.log("[BaseAPI] Retry after refresh succeeded");
          }
        } else {
          // Refresh failed - logout the user
          console.log("[BaseAPI] Token refresh failed - no data in response");
          if (refreshResult.error) {
            console.error("[BaseAPI] Refresh error:", refreshResult.error);
          }
          api.dispatch({ type: "auth/logout" });
        }
      } catch (error) {
        console.error("[BaseAPI] Error during token refresh:", error);
        api.dispatch({ type: "auth/logout" });
      } finally {
        // Release the mutex
        release();
      }
    } else {
      console.log(
        "[BaseAPI] Another request is already refreshing, waiting...",
      );
      // Wait for the in-progress refresh to complete
      await mutex.waitForUnlock();
      console.log(
        "[BaseAPI] Refresh completed by another request, retrying original request",
      );
      // Retry the original request
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

/**
 * Base API configuration for RTK Query
 * All endpoint definitions will inject into this base API
 */
const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  // Define tag types for cache invalidation
  tagTypes: [
    "User",
    "Post",
    "PostCategory",
    "Comment",
    "Grievance",
    "Event",
    "Document",
    "Reservation",
    "HallSpace",
    "Leadership",
    "Role",
    "Department",
    "AuditLog",
    "Analytics",
    "Task",
    "Invitation",
    "RegistrationCode",
    "PendingUser",
    "EboardUpdate",
    "ForumThread",
    "ForumReply",
    "ForumCategory",
    "PoliticalJurisdiction",
    "PoliticalOfficial",
    "PoliticalStaffer",
    "OfficialStance",
    "PoliticalInteraction",
    "PoliticalCommittee",
    "PoliticalDocument",
    "PoliticalIssue",
    "Poll",
    "PollResponse",
    "Vote",
    "VoteParticipation",
    "RepFinder",
    "FederationDelegation",
    "FederationPost",
    "FederationContract",
    "FederationLeadership",
    "FederationOfficial",
    "FederationJurisdiction",
    "FederationAuditLog",
    "StoreProduct",
    "StoreCategory",
    "StoreOrder",
    "StoreSettings",
    "StoreInventory",
    "StoreSizeInventory",
    "SuggestionTopic",
    "Suggestion",
    "PeerSupportWorker",
    "PeerSupportRequest",
    "PeerSupportStats",
    "PeerSupportSettings",
    "PeerSupportCategory",
    "PeerSupportDisposition",
    "OnCallQueue",
    "OnCallStatus",
    "OnCallVoicemail",
    "EmailBlast",
    "Contract",
    "ContractHistory",
    "ArticleHistory",
    "MOU",
    "CalendarToken",
  ],
  // Endpoints will be injected by endpoint files
  endpoints: () => ({}),
});

export { baseApi };
